import {
  Component,
  OnInit,
  ViewChild,
  OnDestroy,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  signal,
  ElementRef,
  AfterContentChecked,
  effect,
  inject,
  WritableSignal,
} from '@angular/core';
import {
  FormlyFieldConfig,
  FieldArrayType,
  FormlyModule,
} from '@ngx-formly/core';
import { GridComponent, GridModule } from '@progress/kendo-angular-grid';
import { InvestmentWizardService } from '../../../investment-wizard/services/investment-wizard-service.service';
import { takeUntil, tap } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { SessionStorageService } from '../../../services/session-storage.service';
import { MatDialog } from '@angular/material/dialog';
import { WarnDialogComponent } from '../../../warn-dialog/warn-dialog.component';
import { FormArray } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CommonModule, Location } from '@angular/common';
import { DocumentReaderService } from '../../../services/document-reader.service';
import { UnifiedFlowService } from '../../../unified-flow/unified.service';
import { FormService } from '../../form-service.service';
import { MaskService } from '../../../services/mask.service';
import { FormBuilderService } from '../../form-builder.service';

import { FormCalculatorComponent } from '../form-calculator/form-calculator.component';
import { NgxLoadingModule } from 'ngx-loading';
import { MatSelectModule } from '@angular/material/select';
import { FiveTwoNinePickerComponent } from '../five-two-nine-picker/five-two-nine-picker.component';
import { FundSearchComponent } from '../../../shared-components/fund-search/fund-search.component';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';

@Component({
  selector: 'app-form-grid',
  templateUrl: './form-grid.component.html',
  styleUrls: ['form-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  imports: [
    FormCalculatorComponent,
    NgxLoadingModule,
    MatSelectModule,
    FiveTwoNinePickerComponent,
    FundSearchComponent,
    MatIconModule,
    GridModule,
    FormlyModule,
    MatTooltipModule,
    CommonModule,
    MatButtonModule,
  ],
})
export class FormlyGridComponent
  extends FieldArrayType
  implements OnInit, OnDestroy, AfterContentChecked
{
  private iws = inject(InvestmentWizardService);
  private formSvc = inject(FormService);
  private ss = inject(SessionStorageService);
  private dialog = inject(MatDialog);
  private snacky = inject(MatSnackBar);
  private location = inject(Location);
  private changeDetector = inject(ChangeDetectorRef);
  private unfService = inject(UnifiedFlowService);
  private docReadSvc = inject(DocumentReaderService);
  private mask = inject(MaskService);
  private formBuilderService = inject(FormBuilderService);

  amountAvailableVarNames: any;
  amountAvailable: any;
  amountAllocatedVarNames: any;
  amountAllocated: number;
  amountAllocatedSubVarNames: any;
  amountLeft: number;
  detector: any;
  updateExternalCalcValue = signal(null);
  percentAllocatedVarNames: any;
  percentAllocated: number;
  percentAllocatedSubVarNames: any;
  percentLeft: number;
  canSearchInvestments: boolean;

  constructor() {
    super();
    this.updateExternalCalcValue = this.formSvc.updateExternalCalcValue;

    effect(() => {
      if (this.updateExternalCalcValue() && this.field['showCalculator']) {
        if (this.ss.get('currentApp') === 'iw') {
          this.getAndCalcAmountIW();
        } else if (this.ss.get('currentApp') === 'pp') {
          this.getAndCalcAmountPP();
        }
      }
    });
  }

  @ViewChild('grid') public grid: GridComponent;
  @ViewChild('fundSearch') public fundSearch;
  @ViewChild('csvUpload') csvUploadVariable!: ElementRef;

  public fileName: string;
  public file: File;
  columns;
  gridData;
  loading = false;
  noRecords = 'Please Add an Entry';
  searchOptions = null;
  showTemplates = false;
  placeholder = 'Select a template to apply';
  label = 'Select a template to apply';
  templates;
  firmTemplates;
  program;
  buttonOption;
  externalButtonOption: string;
  hideDelete = false;
  rights = this.ss.get('rights');
  totalInvestment = 0;
  assetAmount = '$0';
  mode = 'amount';
  totalPercent = 0;
  showTotals = false;
  totalsCondit;
  idVar;
  tspIdVar;
  parentIdVar;
  tspParentIdVar;
  needsRowId = false;
  rowIdPrefix;
  tspRowIdPrefix;
  rowId;
  tspRowId;
  investmentProgramLabel = '';
  fiveTwoNineSearch = false;
  plans529 = [];
  selected529 = { PlanId: null, PlanName: null };
  previous529 = { PlanId: null, PlanName: null };
  unsubscribe = new Subject();
  clear529 = signal(false);
  uploadingFile = false;
  disableTemplates = true;
  canUseFundLookupPopulateBtn = false;
  fundLookupLoading = false;

  ngOnInit() {
    this.onPopulate(this.field);
    const vars = this.field.fieldArray['fieldGroup'].map(x => x.key);
    const parentVars = this.field.parent.fieldGroup.map(x => x.key);
    this.parentIdVar = parentVars.find((x: string) =>
      x.includes('SourceBridgeID')
    ) as string;

    this.tspParentIdVar = parentVars.find((x: string) =>
      x.includes('TSPSourceID')
    ) as string;

    this.idVar = vars.find((x: string) =>
      x.includes('SourceBridgeID')
    ) as string;
    this.tspIdVar = vars.find((x: string) =>
      x.includes('TSPSourceID')
    ) as string;

    if (this.idVar || this.tspIdVar) {
      this.needsRowId = true;
      this.rowId = this.field.parent.model[this.parentIdVar];
      this.rowIdPrefix = this.idVar ? this.idVar.split('_')[0] : this.idVar;
      this.tspRowId = this.field.parent.model[this.tspParentIdVar];
      this.tspRowIdPrefix = this.tspIdVar
        ? this.tspIdVar.split('_')[0]
        : this.tspIdVar;
    }

    this.buttonOption = this.field.fieldArray['props'].btnText;
    this.externalButtonOption = this.field.fieldArray['props'].externalBtnText;

    this.showTemplates =
      this.field.fieldArray['props'].showTemplates &&
      this.rights.includes('IWTemplates');
    this.canUseFundLookupPopulateBtn =
      this.field.parent.parent.parent.model.UnitExternalBridge_Include;
    this.searchOptions = this.field.fieldArray['props'].search;
    if (this.searchOptions) {
      this.checkCanSearchInvestments();
      this.field.parent.parent.parent.form.valueChanges
        .pipe(tap())
        .subscribe(() => {
          this.changeDetector.markForCheck();
        });
    }
    this.getColumnData();
    this.totalsCondit = this.field.fieldArray['props']['showTotals'];
    this.hideDelete = this.field.fieldArray['props'].hideDelete;
    this.noRecords = this.field.props.placeholder
      ? this.field.props.placeholder
      : this.noRecords;

    if (this.showTemplates) {
      this.getTemplates(
        this.field.parent.model.ValidateProduct.InvestmentValidation_Program
      );
    }

    if (this.searchOptions) {
      if (this.searchOptions.fundtype.includes('529')) {
        this.fiveTwoNineSearch = true;

        const planId = this.field.parent.fieldGroup.find(
          x => x.key === 'Selected529PlansArray_PlanID'
        )?.formControl;
        const planName = this.field.parent.fieldGroup.find(
          x => x.key === 'Selected529PlansArray_PlanName'
        )?.formControl;
        this.selected529.PlanId = planId?.value;
        this.selected529.PlanName = planName?.value;
        this.previous529.PlanId = planId?.value;
        this.previous529.PlanName = planName?.value;
      }

      this.program =
        this.field.parent.model.ValidateProduct?.InvestmentValidation_Program;

      if (
        !this.program &&
        this.field.parent.parent.parent.model.ValidateProduct
      ) {
        this.program =
          this.field.parent.parent.parent.model.ValidateProduct
            ?.InvestmentValidation_Program;
      }

      if (
        this.field.parent.model.UNITPushToUI_NonFilteredPrograms &&
        this.field.parent.model.UNITPushToUI_NonFilteredPrograms.includes(
          this.field.parent.model.ValidateProduct.InvestmentValidation_Program
        )
      ) {
        this.program = null;
      }

      this.field.form.valueChanges
        .pipe(takeUntil(this.unsubscribe))
        .subscribe(z => {
          if (this.formSvc.getNeedToUpdateCalc()) {
            this.formSvc.setUpdateExternalCalcValue({ source: 'formGrid' });
          }
          if (z.InvestmentValidation_PickPctOrAmt === 'P') {
            this.mode = 'percent';
          } else {
            this.mode = 'amount';
          }

          const newProgram = z.ValidateProduct?.InvestmentValidation_Program;
          if (newProgram && this.program !== newProgram) {
            this.program = newProgram;
            this.getTemplates(newProgram);
          }

          if (
            this.field.parent.model?.UNITPushToUI_NonFilteredPrograms &&
            this.field.parent.model?.UNITPushToUI_NonFilteredPrograms?.includes(
              this.field.parent.model?.ValidateProduct
                ?.InvestmentValidation_Program
            )
          ) {
            this.program = null;
          }
        });
    }

    this.setProgramLabel();

    if (this.field.key === 'exchange' || this.needsRowId) {
      this.field.fieldGroup.forEach((group, idx) => {
        group.fieldGroup.forEach(x => {
          if (x.key === 'CurrentAnnuityDetailsArray_IsExchange') {
            group['isExchange'] = x.formControl.value;
          }

          const fieldKey = x.key as string;

          if (fieldKey.includes('SourceBridgeID')) {
            if (!x.formControl.value) {
              x.formControl.setValue(`${this.rowIdPrefix}-${idx}`);
            }
          }

          if (fieldKey.includes('TSPSourceID')) {
            if (!x.formControl.value) {
              x.formControl.setValue(`TSPFundDetails-${idx}`);
            }
          }
        });
      });
    }

    this.form.valueChanges.pipe(tap()).subscribe(change => {
      if (change?.ValidateProduct?.InvestmentValidation_Program) {
        this.clear529.set(true);
        this.changeDetector.markForCheck();
      }

      this.setProgramLabel();
    });

    this.checkRequiredHeaders();

    if (this.field['showCalculator']) {
      this.formSvc.needToUpdateCalc = true;
      if (this.ss.get('currentApp') === 'iw') {
        this.mode = this.field.parent.model.InvestmentValidation_PickPctOrAmt;
        this.getAndCalcAmountIW();
      } else if (this.ss.get('currentApp') === 'pp') {
        this.mode = 'A';
        this.getAndCalcAmountPP();
      }
    }
    if (this.docReadSvc.currentDataDetail) {
      this.applyParsedDocValues(this.docReadSvc.currentDataDetail);
      this.unfService.setLoading(false);
    }

    this.docReadSvc.parsedDocDataDetail$
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: docDataDetail => {
          this.applyParsedDocValues(docDataDetail);
          this.unfService.setLoading(false);
        },
        error: error => alert(error),
      });

    if (
      this.field.fieldArray['props'].minEntries &&
      this.field.model.length === 0
    ) {
      this.add();
    }
    this.options.build();
  }

  ngOnDestroy() {
    this.unsubscribe.next(null);
    this.unsubscribe.complete();
  }

  ngAfterContentChecked() {
    this.getColumnData();
    if (this.searchOptions) {
      this.checkCanSearchInvestments();
    }
  }

  getColumnData() {
    this.columns = this.field.fieldArray['fieldGroup'].filter(
      x => x.props.group === this.field.key
    );
    this.columns = this.columns.map(el => {
      const fld = {
        label:
          (Object.prototype.hasOwnProperty.call(
            el.expressions,
            'props.label'
          ) &&
            el.expressions['props.label']?.()) ||
          el.props.label ||
          el.props.placeholder,
        key: el.key,
        help: el.props.help,
        visuallyHidden: el.className.includes('visual-hidden'),
        hide: el.expressions.hide?.() ?? false,
      };
      return fld;
    });
  }

  checkCanSearchInvestments() {
    this.canSearchInvestments =
      !this.searchOptions.conditional ||
      this.formBuilderService.evalConditional(
        null,
        this.searchOptions.conditional,
        null,
        this.field
      );
  }

  setProgramLabel() {
    this.investmentProgramLabel =
      this.field.parent?.model?.ProgramDefinition_DisplayName &&
      this.field.parent?.model?.ProgramDefinition_ProgramID
        ? this.field.parent?.model?.ProgramDefinition_DisplayName[
            this.field.parent?.model?.ProgramDefinition_ProgramID?.indexOf(
              this.field.parent?.model?.ValidateProduct
                ?.InvestmentValidation_Program
            )
          ]
        : '';
  }

  add(fund?, amount?, percent?) {
    super.add();
    const row = this.model.length > 0 ? this.model.length - 1 : 0;
    const manuallyEnteredInvestment = this.columns.find(x =>
      x.key.includes('ManuallyEnteredInvestment')
    )?.key;
    const manuallyEnteredInvestmentField = this.getField(
      this.field,
      { key: manuallyEnteredInvestment },
      row
    );

    if (
      manuallyEnteredInvestmentField &&
      manuallyEnteredInvestmentField.formControl
    ) {
      manuallyEnteredInvestmentField.formControl.setValue(true);
      manuallyEnteredInvestmentField.model[manuallyEnteredInvestment] = true;
    }

    if (fund) {
      if (
        manuallyEnteredInvestmentField &&
        manuallyEnteredInvestmentField.formControl
      ) {
        manuallyEnteredInvestmentField.formControl.setValue(
          fund.ManuallyEnteredInvestment
        );
        manuallyEnteredInvestmentField.model[manuallyEnteredInvestment] =
          fund.ManuallyEnteredInvestment;
      }

      const fundId = this.columns.find(x => x.key.includes('FundID'))?.key;
      const fundIdField = this.getField(this.field, { key: fundId }, row);
      const fundName = this.columns.find(x => x.key.includes('Name'))?.key;
      const fundNameField = this.getField(this.field, { key: fundName }, row);
      const fundType = this.columns?.find(x => x.key.includes('FundType'))?.key;
      const fundTypeField = this.getField(this.field, { key: fundType }, row);
      const expenseRatio = this.columns.find(x =>
        x.key.includes('ExpenseRatio')
      )?.key;
      const expenseRatioField = this.getField(
        this.field,
        { key: expenseRatio },
        row
      );
      const tradeStatus = this.columns?.find(x =>
        x.key.includes('TradeStatus')
      )?.key;
      const tradeStatusField = this.getField(
        this.field,
        { key: tradeStatus },
        row
      );

      const shareClass = this.field?.fieldArray?.['fieldGroup']?.find(
        fieldObj => fieldObj.key.includes('ShareClass')
      )?.key;
      const shareClassField = this.getField(
        this.field,
        { key: shareClass },
        row
      );

      if (
        Object.prototype.hasOwnProperty.call(fund, 'FrontLoadSchedule') &&
        fund['FrontLoadSchedule']
      ) {
        const frontLoadSchedule = this.columns?.find(x =>
          x.key.includes('FrontLoadSchedule')
        )?.key;
        const frontLoadScheduleField = this.getField(
          this.field,
          { key: frontLoadSchedule },
          row
        );

        if (
          frontLoadScheduleField &&
          Object.keys(frontLoadScheduleField).length > 1
        ) {
          frontLoadScheduleField.formControl.setValue(fund.FrontLoadSchedule);
          frontLoadScheduleField.model[frontLoadSchedule] =
            fund.FrontLoadSchedule;
          this.changeDetector.detectChanges();
        }
      }
      if (
        Object.prototype.hasOwnProperty.call(fund, 'CDSCSchedule') &&
        fund['CDSCSchedule']
      ) {
        const CDSCSchedule = this.columns?.find(x =>
          x.key.includes('CDSCSchedule')
        )?.key;
        const CDSCScheduleField = this.getField(
          this.field,
          { key: CDSCSchedule },
          row
        );
        if (CDSCScheduleField && Object.keys(CDSCScheduleField).length > 1) {
          CDSCScheduleField.formControl.setValue(fund.CDSCSchedule);
          CDSCScheduleField.model[CDSCSchedule] = fund.CDSCSchedule;
          this.changeDetector.detectChanges();
        }
      }
      if (
        Object.prototype.hasOwnProperty.call(fund, 'RedemptionFeeSchedule') &&
        fund['RedemptionFeeSchedule']
      ) {
        const redemptionFeeSchedule = this.columns?.find(x =>
          x.key.includes('RedemptionFeeSchedule')
        )?.key;
        const redemptionFeeScheduleField = this.getField(
          this.field,
          { key: redemptionFeeSchedule },
          row
        );
        if (
          redemptionFeeScheduleField &&
          Object.keys(redemptionFeeScheduleField).length > 1
        ) {
          redemptionFeeScheduleField.formControl.setValue(
            fund.RedemptionFeeSchedule
          );
          redemptionFeeScheduleField.model[redemptionFeeSchedule] =
            fund.RedemptionFeeSchedule;
          this.changeDetector.detectChanges();
        }
      }
      if (
        Object.prototype.hasOwnProperty.call(fund, 'RBCategoryID') &&
        fund['RBCategoryID']
      ) {
        const RBCategoryId = this.columns?.find(x =>
          x.key.includes('RBCategoryId')
        )?.key;
        const RBCategoryIdField = this.getField(
          this.field,
          { key: RBCategoryId },
          row
        );
        if (RBCategoryIdField && Object.keys(RBCategoryIdField).length > 1) {
          RBCategoryIdField.formControl.setValue(fund.RBCategoryID);
          RBCategoryIdField.model[RBCategoryId] = fund.RBCategoryID;
          this.changeDetector.detectChanges();
        }
      }
      if (
        Object.prototype.hasOwnProperty.call(fund, 'UITStandardCD') &&
        fund['UITStandardCD']
      ) {
        const UITStandardCD = this.columns?.find(x =>
          x.key.includes('UITStandardCD')
        )?.key;
        const UITStandardCDField = this.getField(
          this.field,
          { key: UITStandardCD },
          row
        );
        if (UITStandardCDField && Object.keys(UITStandardCDField).length > 1) {
          UITStandardCDField.formControl.setValue(fund.UITStandardCD);
          UITStandardCDField.model[UITStandardCD] = fund.UITStandardCD;
          this.changeDetector.detectChanges();
        }
      }
      if (
        Object.prototype.hasOwnProperty.call(fund, 'UITStandardDeferred') &&
        fund['UITStandardDeferred']
      ) {
        const UITStandardDeferred = this.columns?.find(x =>
          x.key.includes('UITStandardDeferred')
        )?.key;
        const UITStandardDeferredField = this.getField(
          this.field,
          { key: UITStandardDeferred },
          row
        );
        if (
          UITStandardDeferredField &&
          Object.keys(UITStandardDeferredField).length > 1
        ) {
          UITStandardDeferredField.formControl.setValue(
            fund.UITStandardDeferred
          );
          UITStandardDeferredField.model[UITStandardDeferred] =
            fund.UITStandardDeferred;
          this.changeDetector.detectChanges();
        }
      }
      if (
        Object.prototype.hasOwnProperty.call(
          fund,
          'UITStandardOrganizationCost'
        ) &&
        fund['UITStandardOrganizationCost']
      ) {
        const UITStandardOrganizationCost = this.columns?.find(x =>
          x.key.includes('UITStandardOrganizationCost')
        )?.key;
        const UITStandardOrganizationCostField = this.getField(
          this.field,
          { key: UITStandardOrganizationCost },
          row
        );
        if (
          UITStandardOrganizationCostField &&
          Object.keys(UITStandardOrganizationCostField).length > 1
        ) {
          UITStandardOrganizationCostField.formControl.setValue(
            fund.UITStandardOrganizationCost
          );
          UITStandardOrganizationCostField.model[UITStandardOrganizationCost] =
            fund.UITStandardOrganizationCost;
          this.changeDetector.detectChanges();
        }
      }
      if (
        Object.prototype.hasOwnProperty.call(fund, 'UITTerminationDate') &&
        fund['UITTerminationDate']
      ) {
        const UITTerminationDate = this.columns?.find(x =>
          x.key.includes('UITTerminationDate')
        )?.key;
        const UITTerminationDateField = this.getField(
          this.field,
          { key: UITTerminationDate },
          row
        );
        if (
          UITTerminationDateField &&
          Object.keys(UITTerminationDateField).length > 1
        ) {
          UITTerminationDateField.formControl.setValue(fund.UITTerminationDate);
          UITTerminationDateField.model[UITTerminationDate] =
            fund.UITTerminationDate;
          this.changeDetector.detectChanges();
        }
      }

      if (fundTypeField && fundTypeField.formControl) {
        fundTypeField.formControl.setValue(fund.FundType);
        setTimeout(() => {
          fundTypeField.model[fundType] = fund.FundType;
          this.changeDetector.detectChanges();
        });
      }

      if (fundIdField && fundIdField.formControl) {
        fundIdField.formControl.setValue(fund.FundID);
        fundIdField.model[fundId] = fund.FundID;
      }

      if (fundNameField && fundNameField.formControl) {
        setTimeout(() => {
          fundNameField.formControl.setValue(fund.FundName || fund.Name);
          this.changeDetector.detectChanges();
        });
      }

      if (expenseRatioField && expenseRatioField.formControl) {
        setTimeout(() => {
          expenseRatioField.formControl.setValue(fund.ExpenseRatio);
          this.changeDetector.detectChanges();
        });
      }

      if (shareClassField && shareClassField.formControl) {
        setTimeout(() => {
          shareClassField.formControl.setValue(fund.ShareClass);
          this.changeDetector.detectChanges();
        });
      }

      if (tradeStatusField && tradeStatusField.formControl) {
        setTimeout(() => {
          tradeStatusField.formControl.setValue(fund.TradeStatus);
          this.changeDetector.detectChanges();
        });
      }
    }

    if (amount) {
      const fundAmount = this.columns.find(x => x.key.includes('Amount')).key;
      const fundAmountField = this.getField(
        this.field,
        { key: fundAmount },
        row
      );

      if (fundAmountField) {
        setTimeout(() => {
          const setAmount =
            typeof amount === 'string' ? amount : amount.toString();
          fundAmountField.formControl.setValue(setAmount);
          fundAmountField.model[fundAmount] = amount;
          this.changeDetector.detectChanges();
        });
      }
      this.totalInvestment += +this.mask.removeMoneyMask(amount);
    }

    if (percent) {
      const fundPercent = this.columns.find(x => x.key.includes('Percent')).key;
      const fundPercentField = this.getField(
        this.field,
        { key: fundPercent },
        row
      );

      if (fundPercentField) {
        fundPercentField.formControl.setValue(`${percent}%`);
        this.changeDetector.detectChanges();
      }
      this.totalInvestment +=
        this.makeNumber(
          this.field.parent.model.ValidateProduct
            .InvestmentWizard_AssetAmount || 0
        ) *
        (percent / 100);
      this.totalPercent =
        this.makeNumber(
          this.field.parent.model.ValidateProduct
            .InvestmentWizard_AssetAmount || 0
        ) / this.totalInvestment;
    }

    this.focusCell(row, 1);

    if (this.needsRowId) {
      const fieldIdVar =
        this.field.parent.model.CurrentNonERISADetailsArray_TSP === 'Y'
          ? this.tspIdVar
          : this.idVar;

      let existingRowValues = this.model.map(x => {
        try {
          if (this.field.parent.model.CurrentNonERISADetailsArray_TSP === 'Y') {
            return +x[this.tspIdVar].split('_')[1];
          } else {
            return +x[this.idVar].split('_')[1];
          }
        } catch (error) {
          return null;
        }
      });

      // const rowId = this.getField(this.field, { key: fieldIdVar }, row);
      // if (rowId && rowId.formControl) {
      //   rowId.formControl.setValue(this.rowId);
      // }
      // this.model[row][this.idVar] = this.rowId; //had to add this in for some reason. May need it on other nested looping groups

      existingRowValues = existingRowValues.filter(
        x => x !== null || x !== undefined
      );

      const rowPrefix =
        this.field.parent.model.CurrentNonERISADetailsArray_TSP === 'Y'
          ? this.tspRowIdPrefix
          : this.rowIdPrefix;
      const highestIdx =
        existingRowValues.length > 0 ? Math.max(...existingRowValues) : null;
      let newIdx = 0;

      if (this.parentIdVar || this.tspParentIdVar) {
        newIdx = +this.field.parent.key;
      } else {
        newIdx = !highestIdx || row > highestIdx ? row : highestIdx + 1;
      }

      const parentIdField =
        this.field.parent.model.CurrentNonERISADetailsArray_TSP === 'Y'
          ? this.field.parent.get(this.tspParentIdVar)
          : this.field.parent.get(this.parentIdVar);
      const idField = this.getField(this.field, { key: fieldIdVar }, row);
      let idValue;

      if (this.field.parent.model.CurrentNonERISADetailsArray_TSP === 'Y') {
        if (!this.field.parent.model[this.tspParentIdVar]) {
          this.field.parent.model[this.tspParentIdVar] =
            `${rowPrefix}-${newIdx}`;
        }
        idValue = this.tspParentIdVar
          ? `${this.field.parent.model[this.tspParentIdVar]}`
          : `${rowPrefix}-${newIdx}`;
        parentIdField.formControl.setValue(`${rowPrefix}-${newIdx}`);
      } else {
        if (!this.field.parent.model[this.parentIdVar]) {
          this.field.parent.model[this.parentIdVar] = `${rowPrefix}-${newIdx}`;
          parentIdField.formControl.setValue(`${rowPrefix}-${newIdx}`);
        }

        idValue = this.parentIdVar
          ? `${this.field.parent.model[this.parentIdVar]}`
          : `${rowPrefix}-${newIdx}`;
      }

      idField?.formControl.setValue(idValue);
      this.field.model[row][fieldIdVar] = idValue;
    }
    this.checkRequiredHeaders();
    this.onPopulate(this.field);
    this.options.build();
  }

  checkRequiredHeaders() {
    const requiredFields = [];
    this.field.fieldArray['fieldGroup'].forEach(x => {
      if (
        x.props.formChecks &&
        x.props.formChecks.includes('required') &&
        x.props.label
      ) {
        requiredFields.push(x.props.label);
      }
    });
    if (requiredFields && requiredFields.length > 0) {
      this.columns.forEach(column => {
        if (requiredFields.includes(column.label)) {
          column.label = column.label + ' *';
        }
      });
    }
  }

  onFileSelected(event) {
    if (
      event.target.files[0].type === 'text/csv' ||
      event.target.files[0].type ===
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ) {
      this.file = event.target.files[0];

      if (this.file) {
        this.fileName = this.file.name;
      }
    } else {
      this.snacky.open(`Warning! File type not supported.`, 'Close', {
        duration: 4000,
      });
    }
  }

  completeCsvUpload() {
    const formData = new FormData();
    formData.append('Spreadsheet', this.file);
    this.uploadingFile = true;
    this.iws.uploadHoldingsFile(formData).subscribe({
      next: ({ details, data }) => {
        if (details.FileUploadSuccess === true && data) {
          data.map(fundObj => {
            fundObj['ManuallyEnteredInvestment'] = false;
            this.add(fundObj, fundObj.Amount);
          });
          this.uploadingFile = false;
          if (
            details.InvestmentsErrorProcess.length <= 0 &&
            details.InvestmentsNotFound.length <= 0 &&
            !details.InvestmentFailureDetails
          ) {
            this.snacky.open(`File uploaded successfully.`, 'Close', {
              duration: 4000,
            });
          } else {
            if (this.field['showButton']) {
              details.InvestmentsNotFound.map(fundObj => {
                this.add(fundObj, fundObj.Amount);
              });
            } else {
              let displayString = `Investment(s) Not Found:`;
              details.InvestmentsNotFound.forEach(inv => {
                displayString += `</br>${inv.FundID}`;
              });
              if (details.InvestmentsErrorProcess.length > 0) {
                displayString = `Process: ${details.InvestmentsErrorProcess[0]} </br> ${displayString}`;
              }
              if (details.InvestmentFailureDetails) {
                displayString = `${details.InvestmentFailureDetails} </br> ${displayString}`;
              }
              this.dialog.open(WarnDialogComponent, {
                panelClass: 'warn-dialog',
                data: {
                  headline: 'Warning',
                  content: displayString,
                  confirm: 'OK',
                },
              });
            }
          }
        }
        this.fileName = null;
        this.file = null;
        this.csvUploadVariable.nativeElement.value = '';
      },
      error: () => {
        this.snacky.open(`An unexpected error occurred.`, 'Close', {
          duration: 4000,
        });
        this.fileName = null;
        this.file = null;
        this.csvUploadVariable.nativeElement.value = '';
      },
    });
  }

  showDelete() {
    const minEntries = this.field.fieldArray['props'].minEntries;
    return (
      !this.hideDelete &&
      !this.field.form.disabled &&
      (!minEntries || (this.field.model?.length ?? 0) > 1)
    );
  }

  remove(idx, ev?, removeAll = false) {
    if (ev) {
      ev.preventDefault();
    }

    const localModel = [...this.model],
      deletedFund = localModel[idx],
      model = this.field.parent?.parent?.parent?.model,
      key = this.key as string,
      varSet = this.columns[0].key.split('_')[0],
      fundId = `${varSet}_FundID`,
      idVar = this.idVar?.split('_')[1],
      sourceBridge = `${varSet}_${idVar}`,
      fundIdx = model[key]
        ? model[key].findIndex(
            x =>
              deletedFund &&
              x &&
              x[fundId] === deletedFund[fundId] &&
              x[sourceBridge] === this.rowId
          )
        : -999;

    if (
      fundIdx > -1 &&
      this.field.fieldArray &&
      this.field.fieldArray['props'].nested
    ) {
      model[key].splice(fundIdx, 1);
    }

    const planId = this.field.parent.fieldGroup.find(
      x => x.key === 'Selected529PlansArray_PlanID'
    )?.formControl;

    if (planId) {
      this.selected529 = { PlanId: planId.value, PlanName: null };
    }

    const removeIdx = this.model[idx] ? idx : fundIdx;
    if (removeAll) {
      let i = this.model.length;
      while (i--) {
        // Manually remove instead of using super.remove() which is removing 2 items
        const formArray = this.formControl as FormArray;

        // Remove from the model array (what the grid displays)
        this.model.splice(i, 1);

        // Remove from fieldGroup
        this.field.fieldGroup.splice(i, 1);

        // Remove from form control
        formArray.removeAt(i);
        formArray.markAsDirty();

        // Update field array keys to prevent duplicate entries
        this.field.fieldGroup.forEach((fieldGroup, index) => {
          fieldGroup.key = index.toString();
        });
      }
      this.model.length = 0;
    } else {
      // Manually remove instead of using super.remove() which is removing 2 items
      const formArray = this.formControl as FormArray;

      // Remove from the model array (what the grid displays)
      this.model.splice(removeIdx, 1);

      // Remove from fieldGroup
      this.field.fieldGroup.splice(removeIdx, 1);

      // Remove from form control
      formArray.removeAt(removeIdx);
      formArray.markAsDirty();

      // Update field array keys to prevent duplicate entries
      this.field.fieldGroup.forEach((fieldGroup, index) => {
        fieldGroup.key = index.toString();
      });
    }

    // Trigger formly field check instead of manual change detection
    this.field.options.checkExpressions?.(this.field);
  }

  focusCell(row, cell) {
    if (this.grid) {
      this.grid.focusCell(row, cell);
    }
  }

  getField(
    field: FormlyFieldConfig,
    column,
    rowIndex: number
  ): FormlyFieldConfig {
    const fieldDef = field.fieldGroup[rowIndex]
      ? field.fieldGroup[rowIndex].fieldGroup.find(f => f.key === column.key)
      : null;
    return fieldDef ? fieldDef : { hide: false };
  }

  checkHighlightRow(field, rowIndex) {
    if (field.key && field.key === 'SelectedFunds') {
      if (
        field.fieldGroup[rowIndex].fieldGroup.find(
          fieldItem => fieldItem.key === 'SelectedFunds_FundID'
        ).props.highlightRow === true
      ) {
        return true;
      }
    }
    return false;
  }

  hideColumn(
    field: FormlyFieldConfig,
    column,
    rowIndex: number
  ): FormlyFieldConfig {
    let fieldVisible;
    if (field.formControl instanceof FormArray) {
      fieldVisible = field.fieldArray['fieldGroup']
        ? field.fieldArray['fieldGroup'].find(f => f.key === column.key)
        : false;
    } else {
      fieldVisible = field.fieldGroup[rowIndex]
        ? field.fieldGroup[rowIndex].fieldGroup.find(f => f.key === column.key)
        : false;
    }

    return fieldVisible.expressions.hide?.() ?? null;
  }

  getModel(model, rowIndex) {
    return model[rowIndex];
  }

  handleFund(ev) {
    let fundInfo = ev.option.value;
    this.iws
      .fundInfo(ev.option.value.FundID)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(extraFundInfo => {
        if (extraFundInfo.results[0]) {
          fundInfo = { ...fundInfo, ...extraFundInfo.results[0] };
        }
        fundInfo['ManuallyEnteredInvestment'] = false;
        this.add(fundInfo);
      });
  }

  checkApplyTemplate(ev) {
    let typeAmount = 0;
    if (
      this.field.parent.model.ValidateProduct
        .InvestmentValidation_PurchaseType === 3 ||
      this.field.parent.model.ValidateProduct
        .InvestmentValidation_PurchaseType === '3'
    ) {
      this.field.parent.model.CurrentPosition.forEach(investment => {
        if (
          investment &&
          (investment.CurrentPosition_Sell === 'true' ||
            investment.CurrentPosition_Sell) &&
          investment.CurrentPosition_SellAmount
        ) {
          typeAmount += this.makeNumber(investment.CurrentPosition_SellAmount);
        }
      });
    }

    if (
      (this.field.parent.model.ValidateProduct
        .InvestmentValidation_PurchaseType === '1' &&
        (!this.field.parent.model.ValidateProduct
          .InvestmentWizard_AssetAmount ||
          this.field.parent.model.ValidateProduct
            .InvestmentWizard_AssetAmount === '$0' ||
          this.field.parent.model.ValidateProduct
            .InvestmentWizard_AssetAmount === '0')) ||
      (this.field.parent.model.ValidateProduct
        .InvestmentValidation_PurchaseType === '2' &&
        (!this.field.parent.model.ValidateProduct
          .InvestmentWizard_AddMoneyAmount ||
          this.field.parent.model.ValidateProduct
            .InvestmentWizard_AddMoneyAmount === '$0' ||
          this.field.parent.model.ValidateProduct
            .InvestmentWizard_AddMoneyAmount === '0')) ||
      (this.field.parent.model.ValidateProduct
        .InvestmentValidation_PurchaseType === '3' &&
        typeAmount === 0)
    ) {
      const dialogRef = this.dialog.open(WarnDialogComponent, {
        panelClass: 'warn-dialog',
        data: {
          headline: 'Warning',
          content:
            'You have chosen to apply a template and the entered investment amount is $0.<br/><br/>If you would like to apply the template anyway, press OK.<br/><br/>Otherwise, press Cancel, change the investment amount, and apply the template again.',
          confirm: 'OK',
        },
      });
      dialogRef
        .afterClosed()
        .pipe(takeUntil(this.unsubscribe))
        .subscribe(result => {
          if (result === 'continue') {
            this.applyTemplate(ev);
          } else if (result === 'cancel') {
            ev.source.writeValue(null);
            ev.source.value = null;
          }
        });
    } else {
      this.applyTemplate(ev);
    }
  }

  applyTemplate(ev) {
    let investment = 0;

    if (
      (this.field.parent.model.ValidateProduct
        .InvestmentValidation_PurchaseType === 2 ||
        this.field.parent.model.ValidateProduct
          .InvestmentValidation_PurchaseType === '2') &&
      this.field.parent.model.ValidateProduct.InvestmentWizard_AddMoneyAmount
    ) {
      investment = this.makeNumber(
        this.field.parent.model.ValidateProduct.InvestmentWizard_AddMoneyAmount
      );
    } else if (
      this.field.parent.model.ValidateProduct
        .InvestmentValidation_PurchaseType === 3 ||
      this.field.parent.model.ValidateProduct
        .InvestmentValidation_PurchaseType === '3'
    ) {
      let totalInvestment = 0;
      this.field.parent.model.CurrentPosition.forEach(investment => {
        if (
          investment &&
          (investment.CurrentPosition_Sell === 'true' ||
            investment.CurrentPosition_Sell) &&
          investment.CurrentPosition_SellAmount
        ) {
          totalInvestment += this.makeNumber(
            investment.CurrentPosition_SellAmount
          );
        }
      });
      investment = totalInvestment;
    } else if (
      this.field.parent.model.ValidateProduct.InvestmentWizard_AssetAmount &&
      this.field.parent.model.ValidateProduct.InvestmentWizard_AssetAmount !==
        '$0'
    ) {
      investment = this.makeNumber(
        this.field.parent.model.ValidateProduct.InvestmentWizard_AssetAmount
      );
    }
    const funds = ev.value.funds;

    ev.source.writeValue(null);
    ev.source.value = null;

    let fundList = '';

    funds.forEach((x, i) => {
      if (!x.manual) {
        if (i !== funds.length - 1) {
          fundList += `${x.ticker},`;
        } else {
          fundList += x.ticker;
        }
      } else if (
        x.manual &&
        this.field.parent.model.UNITPushToUI_ManualFundPrograms.includes(
          this.program
        )
      ) {
        const addFund = {
          FundName: x.name,
          FundID: x.ticker,
          ExpenseRatio: null,
          ManuallyEnteredInvestment: false,
        };
        const perc = x.percent * 100;
        const amount = investment * (perc / 100);
        this.add(addFund, amount.toFixed(2), perc);
      }
    });
    let fundInfo;
    const symbolList = [];

    this.iws
      .fundInfo(fundList)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(x => {
        fundInfo = x.results;
        fundInfo.forEach(el => {
          if (!symbolList.includes(el.Symbol)) {
            const fnd = {};
            const info = funds.find(z => {
              return z.ticker === el.Symbol;
            });

            const perc = info.percent * 100;
            const amount = investment * (perc / 100);

            fnd['FundName'] = el.FundName;
            fnd['FundID'] = info.ticker;
            fnd['ExpenseRatio'] = +el.ExpenseRatio;
            fnd['ManuallyEnteredInvestment'] = false;
            fnd['FundType'] = el.FundType;

            symbolList.push(el.Symbol);

            this.add(fnd, amount.toFixed(2), perc);
          }
        });
      });
  }

  getTemplates(program) {
    this.iws
      .getTemplates()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(x => {
        const userTemplates = x.results.UserTemplates;
        const firmTemplates = x.results.FirmTemplates;

        this.templates =
          userTemplates && userTemplates.length > 0
            ? userTemplates.filter(z => z.program === program)
            : [];
        this.firmTemplates =
          firmTemplates && firmTemplates.length > 0
            ? firmTemplates.filter(z => z.program === program)
            : [];

        this.disableTemplates =
          this.templates.length < 1 && this.firmTemplates.length < 1;
        this.changeDetector.detectChanges();
      });
  }

  get529() {
    // let programId =
    //   this.field.parent.model.ValidateProduct?.InvestmentValidation_Program;
    // if (!programId && this.field.parent.parent.parent.model.ValidateProduct) {
    //   programId =
    //     this.field.parent.parent.parent.model.ValidateProduct
    //       ?.InvestmentValidation_Program;
    // }
    // this.iws
    //   .get529(true, programId)
    //   .pipe(takeUntil(this.unsubscribe))
    //   .subscribe(x => {
    //     this.plans529 = [...x.data];
    //     this.changeDetector.detectChanges();
    //   });
  }

  handle529(plan) {
    this.selected529 = { ...plan };

    if (this.selected529) {
      const planId = this.field.parent.fieldGroup.find(
        x => x.key === 'Selected529PlansArray_PlanID'
      )?.formControl;
      const planName = this.field.parent.fieldGroup.find(
        x => x.key === 'Selected529PlansArray_PlanName'
      )?.formControl;
      let cancelSelection = false;

      if (
        planId.value &&
        planId.value !== this.selected529.PlanId &&
        this.model &&
        this.model.length > 0
      ) {
        const dialogRef = this.dialog.open(WarnDialogComponent, {
          panelClass: 'warn-dialog',
          data: {
            headline: 'Warning',
            content:
              'If you choose to make this change your previous fund selections for this entry will be cleared.',
            confirm: 'OK',
          },
        });
        dialogRef
          .afterClosed()
          .pipe(takeUntil(this.unsubscribe))
          .subscribe(result => {
            if (result === 'continue') {
              planId.setValue(this.selected529.PlanId);
              planName.setValue(this.selected529.PlanName);
              this.remove(0, null, true);
            }
            if (result === 'cancel') {
              cancelSelection = true;
              planId.setValue(this.previous529.PlanId);
              planName.setValue(this.previous529.PlanName);
              this.selected529.PlanId = this.previous529.PlanId;
              this.selected529.PlanName = this.previous529.PlanName;
            }
          });
      } else {
        if (planId && !cancelSelection) {
          planId.setValue(this.selected529.PlanId || null);
          this.previous529.PlanId = this.selected529.PlanId;
        }
        if (planName && !cancelSelection) {
          planName.setValue(this.selected529.PlanName || null);
          this.previous529.PlanName = this.selected529.PlanName;
        }
      }
    }
    this.changeDetector.detectChanges();
  }

  makeNumber(num) {
    if (typeof num === 'number') {
      return num;
    } else {
      const formatted = num.replace('$', '').replaceAll(',', '');
      return +formatted;
    }
  }

  getAndCalcAmountIW() {
    if (this.field['amountAvailable']) {
      this.amountAvailableVarNames = this.field['amountAvailable'].split('.');
      let amountAvailableTracker = this.field;
      this.amountAvailableVarNames.map(varName => {
        amountAvailableTracker = amountAvailableTracker[varName];
      });
      this.amountAvailable = this.mask.removeMoneyMask(amountAvailableTracker);
    } else {
      this.amountAvailable = this.calculateTotalAmount();
    }

    if (
      this.mode.toLowerCase() === 'a' ||
      this.mode.toLowerCase() === 'amount'
    ) {
      this.amountAllocatedVarNames = this.field['amountAllocated'].split('|');
      this.amountAllocated = 0;
      this.amountAllocatedVarNames.forEach(varName => {
        this.amountAllocatedSubVarNames = varName.split('.');
        this.amountAllocated += +this.stepToAmount(this.field, 0);
      });
      this.amountLeft = this.amountAvailable - this.amountAllocated;
    } else if (
      this.mode.toLowerCase() === 'p' ||
      this.mode.toLowerCase() === 'percent'
    ) {
      this.amountAllocatedVarNames = this.field['percentAllocated'].split('|');
      this.amountAllocated = 0;
      this.amountAllocatedVarNames.forEach(varName => {
        this.amountAllocatedSubVarNames = varName.split('.');
        this.amountAllocated += +this.stepToAmount(this.field, 0);
      });
      this.percentLeft = 100 - +this.amountAllocated;
    }
    this.changeDetector.detectChanges();
  }

  getAndCalcAmountPP() {
    try {
      this.amountAllocated = 0;
      this.amountAllocatedVarNames = this.field['amountAllocated'].split('.');
      let amountAllocatedTracker = null;
      this.amountAllocatedVarNames.map(varName => {
        if (varName === 'model' || varName === 'localmodel') {
          amountAllocatedTracker = this.model;
        } else {
          amountAllocatedTracker.forEach(tracker => {
            amountAllocatedTracker = tracker[varName];
            if (
              typeof this.mask.removeMoneyMask(amountAllocatedTracker) ===
              'number'
            ) {
              this.amountAllocated += this.mask.removeMoneyMask(
                amountAllocatedTracker
              );
            }
          });
        }
      });

      this.amountAvailable = 0;
      this.amountAvailableVarNames = this.field['amountAvailable'].split('|');
      const showAmountAvailableVarNames =
        this.field.fieldArray['props']?.['showAmountAvailable'];

      // Recursive function to process amountAvailableTracker
      const processTracker = (tracker, varNames, varIndex) => {
        if (
          tracker &&
          (typeof tracker === 'number' || typeof tracker === 'string')
        ) {
          // If tracker is a number, process it
          this.checkHiddenAndAdd(
            showAmountAvailableVarNames,
            varIndex,
            tracker
          );
          return;
        }

        if (Array.isArray(tracker)) {
          // If tracker is an array, iterate through each item
          tracker.forEach(item => {
            const trackerKeys = Object.keys(item);
            let nextVarName = varNames[0];
            let tempNextVarName = `${nextVarName.split('_')[0]}_${
              item[
                trackerKeys.find(trackerKey =>
                  trackerKey.toLowerCase().includes('fullvspartial')
                )
              ] === 'P'
                ? 'PartialAmount'
                : 'CurrentValue'
            }`;
            if (!Object.prototype.hasOwnProperty.call(item, tempNextVarName)) {
              tempNextVarName = nextVarName;
            }
            nextVarName = tempNextVarName;
            varNames[0] = nextVarName;
            processTracker(item, varNames, varIndex);
          });
          return;
        }
        if (
          typeof tracker === 'object' &&
          tracker !== null &&
          varNames.length > 0
        ) {
          // If tracker is an object, progress to the next reference
          const nextVarName = varNames[0]; // Get the next variable name
          if (nextVarName === 'model') {
            processTracker(
              this.field.parent.model,
              varNames.slice(1),
              varIndex
            ); // Pass a copy of varNames without modifying the original
          } else {
            processTracker(tracker[nextVarName], varNames.slice(1), varIndex); // Pass a copy of varNames without modifying the original
          }
        }
      };

      // Iterate through each varGroup
      this.amountAvailableVarNames.forEach((varGroup, varIndex) => {
        const currVarNames = varGroup.split('.'); // Split the varGroup into references
        processTracker(this.field, currVarNames, varIndex); // Start processing
      });

      this.amountLeft = this.amountAllocated - this.amountAvailable;
      this.changeDetector.detectChanges();
    } catch (error) {
      console.error('Error parsing references', error);
    }
  }

  checkHiddenAndAdd(
    showAmountAvailableVarNames,
    varIndex,
    amountAvailableTracker
  ) {
    let showLevel = null;
    if (showAmountAvailableVarNames) {
      // prettier-ignore
      const propertyChain = showAmountAvailableVarNames.split('|')[varIndex].split('.');

      propertyChain.forEach(property => {
        if (property === 'model') {
          showLevel = this.field.parent.model;
        } else {
          showLevel = showLevel[property];
        }
      });
      if (typeof showLevel === 'boolean' && showLevel) {
        this.amountAvailable += this.mask.removeMoneyMask(
          amountAvailableTracker
        );
      }
    } else {
      throw new Error('No references to selected sources');
    }
  }

  stepToAmount(currLevel, varNameIndex): number {
    let runningTotal = 0;
    if (currLevel) {
      if (!Array.isArray(currLevel)) {
        runningTotal += +this.stepToAmount(
          currLevel[this.amountAllocatedSubVarNames[varNameIndex]],
          varNameIndex + 1
        );
      } else {
        currLevel.forEach(levelItem => {
          if (
            Array.isArray(
              levelItem[this.amountAllocatedSubVarNames[varNameIndex]]
            )
          ) {
            runningTotal += +this.stepToAmount(
              levelItem[this.amountAllocatedSubVarNames[varNameIndex]],
              varNameIndex + 1
            );
          } else {
            runningTotal += levelItem[
              this.amountAllocatedSubVarNames[varNameIndex]
            ]
              ? +this.mask.removeMoneyMask(
                  levelItem[this.amountAllocatedSubVarNames[varNameIndex]]
                )
              : 0;
          }
        });
      }
    }
    return runningTotal;
  }

  calculateTotalAmount() {
    let totalAmount = 0;
    if (
      (this.field.parent.model.ValidateProduct
        .InvestmentValidation_PurchaseType === 2 ||
        this.field.parent.model.ValidateProduct
          .InvestmentValidation_PurchaseType === '2') &&
      this.field.parent.model.ValidateProduct.InvestmentWizard_AddMoneyAmount
    ) {
      totalAmount = this.makeNumber(
        this.field.parent.model.ValidateProduct.InvestmentWizard_AddMoneyAmount
      );
    } else if (
      this.field.parent.model.ValidateProduct
        .InvestmentValidation_PurchaseType === 3 ||
      this.field.parent.model.ValidateProduct
        .InvestmentValidation_PurchaseType === '3'
    ) {
      let totalDisplay = 0;
      this.field.parent.model.CurrentPosition.forEach(investment => {
        if (
          ((investment &&
            ((Object.prototype.hasOwnProperty.call(
              investment,
              'CurrentPosition_Sell'
            ) &&
              investment.CurrentPosition_Sell === 'true') ||
              investment.CurrentPosition_Sell === true)) ||
            (investment &&
              Object.prototype.hasOwnProperty.call(
                investment,
                'CurrentPosition_HoldOrSell'
              ) &&
              investment.CurrentPosition_HoldOrSell === 'Sell')) &&
          (investment.CurrentPosition_SellAmount ||
            investment.CurrentPosition_SellAmount === 0)
        ) {
          totalDisplay += +this.makeNumber(
            investment.CurrentPosition_SellAmount
          );
        }
      });
      totalAmount = totalDisplay;
    } else if (
      this.field.parent.model.ValidateProduct.InvestmentWizard_AssetAmount
    ) {
      totalAmount = this.makeNumber(
        this.field.parent.model.ValidateProduct.InvestmentWizard_AssetAmount
      );
    }
    return totalAmount;
  }

  downloadCsvTemplate(ev) {
    ev.preventDefault();
    window.open('assets/IW-Import-Holdings-template.csv', '_blank');
  }

  onLinkClick() {
    if (this.field.parent?.props?.sectionId === 'CurrentHoldingsSell') {
      const fundIds = this.field.formControl.value.map(
        (rowItem: {
          CurrentPosition_FundType: string;
          CurrentPosition_FundID: string;
          CurrentPosition_Name: string;
          CurrentPosition_ExpenseRatio: string;
          CurrentPosition_InitialPurchaseDate: any;
          CurrentPosition_RecommendByFPPurchase: string;
          CurrentPosition_RecommendByFPLiquidation: string;
          CurrentPosition_ShareClass: string;
        }) => rowItem.CurrentPosition_FundID
      );
      if (fundIds.length) {
        this.ss.set('IWProductShelfFilters', {
          logic: 'and',
          filters: [
            {
              field: 'FundID',
              operator: 'contains',
              value: fundIds,
            },
          ],
        });
      }
    }

    window.open(
      `${this.location.prepareExternalUrl('/iw/product-shelf/')}${encodeURIComponent(
        this.investmentProgramLabel
      )}`,
      '_blank'
    );
  }

  fundLookup(): void {
    this.fundLookupLoading = true;
    const symbolList: string[] = [];

    this.model.forEach(fieldValue => {
      if (Object.keys(fieldValue).includes(`${this.rowIdPrefix}_FundID`)) {
        symbolList.push(fieldValue[`${this.rowIdPrefix}_FundID`]);
      }
    });
    this.iws.fundInfo(symbolList).subscribe({
      next: res => {
        res.results.forEach(resItem => {
          const rowToChange = this.field.fieldGroup.find(filterItem => {
            const field = filterItem.fieldGroup.find(fieldItem => {
              return fieldItem.key === `${this.rowIdPrefix}_FundID`;
            });
            return field.formControl.value === resItem.Symbol;
          });

          rowToChange.fieldGroup
            .find(col => col.key === `${this.rowIdPrefix}_Name`)
            .formControl.setValue(resItem.FundName);
          rowToChange.fieldGroup
            .find(col => col.key === `${this.rowIdPrefix}_ExpenseRatio`)
            .formControl.setValue(resItem.ExpenseRatio);
          rowToChange.fieldGroup
            .find(col => col.key === `${this.rowIdPrefix}_ShareClass`)
            .formControl.setValue(resItem.ShareClass);
        });
        this.fundLookupLoading = false;
        this.changeDetector.detectChanges();
      },
      error: () => {
        console.error('Network error');
        this.fundLookupLoading = false;
        this.changeDetector.detectChanges();
      },
    });
  }

  applyParsedDocValues(docDataDetail) {
    this.unfService.setLoading(false);

    if (
      docDataDetail.pageDetail &&
      docDataDetail.pageDetail.sectionId &&
      (!docDataDetail.pageDetail.tabId ||
        docDataDetail.pageDetail.tabId === this.rowId) &&
      (docDataDetail.pageDetail.sectionId ===
        this.field.parent?.parent?.parent?.props.sectionId ||
        docDataDetail.pageDetail.sectionId ===
          this.field.parent?.props.sectionId)
    ) {
      docDataDetail.data.forEach(data => {
        this.add(data, data.Amount);
      });

      this.docReadSvc.currentDataDetail = null;
      this.unfService.setLoading(false);
      this.changeDetector.detectChanges();
    }
  }

  checkShowGrid() {
    return this.columns.some(col => !col.hide);
  }
}
